'use client';

import { ComponentConfig } from "@puckeditor/core";
import { useCart } from "@lib/hooks/useCart";
import { convertToLocale } from "@lib/util/money";

export interface CartProgressBarProps {
  title: string;
  goalType: "free_shipping" | "free_gift" | "discount";
  goalAmount: number;
  rewardLabel: string;
  progressColor: string;
  backgroundColor: string;
  showAmount: boolean;
}

export const CartProgressBar: ComponentConfig<CartProgressBarProps> = {
  label: "Cart Progress Bar",

  fields: {
    title: {
      type: "text",
      label: "Title (before goal)",
    },
    goalType: {
      type: "select",
      label: "Goal Type",
      options: [
        { label: "Free Shipping", value: "free_shipping" },
        { label: "Free Gift", value: "free_gift" },
        { label: "Discount", value: "discount" },
      ],
    },
    goalAmount: {
      type: "number",
      label: "Goal Amount (in cents)",
    },
    rewardLabel: {
      type: "text",
      label: "Reward Label (e.g. 'free shipping')",
    },
    progressColor: {
      type: "text",
      label: "Progress Bar Color (CSS color)",
    },
    backgroundColor: {
      type: "text",
      label: "Background Color (CSS color)",
    },
    showAmount: {
      type: "radio",
      label: "Show Remaining Amount",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
  },

  defaultProps: {
    title: "You're",
    goalType: "free_shipping",
    goalAmount: 5000,
    rewardLabel: "free shipping",
    progressColor: "#111827",
    backgroundColor: "#e5e7eb",
    showAmount: true,
  },

  render({ title, goalAmount, rewardLabel, progressColor, backgroundColor, showAmount }) {
    return (
      <CartProgressBarRenderer
        title={title}
        goalAmount={goalAmount}
        rewardLabel={rewardLabel}
        progressColor={progressColor}
        backgroundColor={backgroundColor}
        showAmount={showAmount}
      />
    );
  },
};

interface RendererProps {
  title: string;
  goalAmount: number;
  rewardLabel: string;
  progressColor: string;
  backgroundColor: string;
  showAmount: boolean;
}

function CartProgressBarRenderer({
  title,
  goalAmount,
  rewardLabel,
  progressColor,
  backgroundColor,
  showAmount,
}: RendererProps) {
  const { cart } = useCart();
  const subtotal = (cart as any)?.subtotal ?? 0;
  const currencyCode = (cart as any)?.currency_code ?? 'usd';
  const reached = subtotal >= goalAmount;
  const progressPct = Math.min(100, (subtotal / goalAmount) * 100);
  const remaining = goalAmount - subtotal;

  return (
    <div className="w-full px-4 py-3">
      {reached ? (
        <p className="text-sm font-medium text-green-700 mb-2">
          🎉 You&apos;ve unlocked {rewardLabel}!
        </p>
      ) : (
        <p className="text-sm font-medium text-gray-700 mb-2">
          {title}{' '}
          {showAmount && (
            <span className="font-semibold">
              {convertToLocale({ amount: remaining, currency_code: currencyCode })}
            </span>
          )}{' '}
          away from <span className="font-semibold">{rewardLabel}</span>
        </p>
      )}

      <div
        className="w-full h-2 rounded-full overflow-hidden"
        style={{ backgroundColor }}
        role="progressbar"
        aria-valuenow={progressPct}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progressPct}%`, backgroundColor: progressColor }}
        />
      </div>
    </div>
  );
}
